import { useState } from "react";
import { FileText, ImageOff, Play, Music, File } from "lucide-react";
import API from "../lib/api";

/**
 * Universal artifact gallery.
 *   - Renders image files as thumbnails; click opens a lightbox.
 *   - Renders non-image files as file-type cards with a name/size/type.
 *   - Fetches file bytes via /api/files/:id (auth-gated by backend).
 *
 * Props:
 *   artifacts: [{ file_id, file_name, content_type, size, file_description, ... }]
 *   canDownload: boolean — whether the current viewer should see a Download link
 */
export function ArtifactGallery({ artifacts = [], canDownload = false }) {
  const [preview, setPreview] = useState(null);

  if (!artifacts.length) {
    return (
      <div className="text-sm text-muted-h italic flex items-center gap-2">
        <ImageOff size={16} /> No files attached to this submission.
      </div>
    );
  }

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const fileUrl = (id) => `${backend}/api/files/${id}`;

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {artifacts.map((a, i) => (
          <ArtifactCard
            key={a.file_id || `${a.file_name || "art"}-${i}`}
            artifact={a}
            src={a.file_id ? fileUrl(a.file_id) : null}
            onOpen={() => a.file_id && isImage(a) && setPreview(a)}
            canDownload={canDownload}
          />
        ))}
      </div>

      {preview && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Preview: ${preview.file_name}`}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="artifact-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white text-3xl leading-none w-10 h-10 rounded-full hover:bg-white/10"
            onClick={() => setPreview(null)}
            aria-label="Close preview"
            data-testid="lightbox-close"
          >×</button>
          <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={fileUrl(preview.file_id)}
              alt={preview.file_description || preview.file_name}
              className="w-full h-auto max-h-[80vh] object-contain rounded"
            />
            <figcaption className="mt-3 text-white text-center text-sm">
              <div className="font-medium">{preview.file_name}</div>
              {preview.file_description && <div className="text-white/70 mt-1">{preview.file_description}</div>}
            </figcaption>
          </figure>
        </div>
      )}
    </>
  );
}

function ArtifactCard({ artifact, src, onOpen, canDownload }) {
  const image = isImage(artifact);
  const Icon = fileIcon(artifact);
  return (
    <figure className="border border-heritage rounded-lg overflow-hidden bg-paper hover-lift" data-testid={`artifact-card-${artifact.file_id || artifact.file_name}`}>
      {image && src ? (
        <button onClick={onOpen} className="block w-full aspect-square bg-parchment overflow-hidden focus:outline-none focus:ring-2 focus:ring-[var(--oih-navy)]" aria-label={`Open ${artifact.file_name}`}>
          <img src={src} alt={artifact.file_description || artifact.file_name || "Artifact image"} className="w-full h-full object-cover" loading="lazy" />
        </button>
      ) : (
        <div className="aspect-square bg-parchment flex flex-col items-center justify-center text-navy p-4">
          <Icon size={40} className="opacity-80 mb-2" />
          <div className="text-xs uppercase tracking-widest text-muted-h">{fileKind(artifact)}</div>
        </div>
      )}
      <figcaption className="p-3">
        <div className="text-sm text-ink font-medium truncate" title={artifact.file_name || "(no file)"}>
          {artifact.file_name || "(no file)"}
        </div>
        {artifact.file_description && <div className="text-xs text-ink-soft mt-1 line-clamp-2">{artifact.file_description}</div>}
        <div className="mt-2 flex items-center justify-between text-[11px] text-muted-h">
          <span>{formatSize(artifact.size)}</span>
          {canDownload && artifact.file_id && (
            <a href={src} download={artifact.file_name} className="text-navy hover:underline" data-testid={`download-${artifact.file_id}`}>Download</a>
          )}
        </div>
      </figcaption>
    </figure>
  );
}

function isImage(a) {
  if (!a) return false;
  const ct = (a.content_type || "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  const n = (a.file_name || "").toLowerCase();
  return /\.(jpe?g|png|gif|webp|heic|heif|bmp|svg)$/.test(n);
}
function fileIcon(a) {
  const ct = (a.content_type || "").toLowerCase();
  const n = (a.file_name || "").toLowerCase();
  if (ct.startsWith("video/") || /\.(mp4|mov|avi|webm)$/.test(n)) return Play;
  if (ct.startsWith("audio/") || /\.(mp3|wav|m4a|ogg)$/.test(n)) return Music;
  if (ct.includes("pdf")     || /\.pdf$/.test(n)) return FileText;
  if (ct.includes("word")    || /\.(docx?|txt|rtf)$/.test(n)) return FileText;
  return File;
}
function fileKind(a) {
  const ct = (a.content_type || "").toLowerCase();
  const n = (a.file_name || "").toLowerCase();
  if (ct.startsWith("video/") || /\.(mp4|mov)$/.test(n)) return "Video";
  if (ct.startsWith("audio/") || /\.(mp3|wav)$/.test(n)) return "Audio";
  if (ct.includes("pdf")     || /\.pdf$/.test(n)) return "PDF";
  if (ct.includes("word")    || /\.docx?$/.test(n)) return "Document";
  return (a.file_name || "").split(".").pop()?.toUpperCase() || "File";
}
function formatSize(bytes) {
  if (!bytes) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
